import { useMemo, useState } from "react";
import {
  Typography,
  ThemeProvider,
  Box,
  Button,
  Link,
  Container,
  Stack,
  Paper,
} from "@mui/material";
import "../../Styles/form.css";
import "../../Styles/general.css";

import ProjectCodeBlock from "./projectCode";
import ProjectDecisions from "./projectDecisions";
import ProjectStatus from "./projectStatus";
import ProjectMetaData from "./projectMetaData";
import ProjectDecisionForm from "./projectDecisionForm";
import { useTheme } from "@emotion/react";
import {
  validateCode,
  generateCode,
  saveCode,
  updateProjectStatus,
} from "../../Controllers/projectController";
import {
  fetchProjectDecisions,
  submitDecision,
} from "../../Controllers/decisionController";
import ChangeTextChip from "./changeTextChip";
import ChangeText from "./changeText";
import { useError } from "../../Context/errorContext";
import ProjectProgress from "./projectProgress";
import SectorCircularProgress from "./Progress/sectorCircularProgress";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import Attachments from "./attachments";
import { allowedExtensions, allowedTypes, statuses } from "../helpers";

const DisplayProject = ({
  uid,
  user,
  decisions,
  setDecisions,
  project,
  unit,
  units,
  activity,
  activities,
  status,
  refreshProject,
  similarProjects,
  fixedCode,
  serialNumber,
  setSerialNumber,
  setCodeFields,
  attachments,
}) => {
  const { setError, setAlert } = useError();
  const [decisionByRole, setDecisionByRole] = useState("");
  const [editCode, setEditCode] = useState(false);
  const [decision, setDecision] = useState(null);
  const [comments, setComments] = useState("");
  /* const [expand, setExpand] = useState(false); */
  const [asAccountant, toggleAccountant] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [isCodeValid, setIsCodeValid] = useState(false);
  const [validating, setValidating] = useState(false);
  const [syncToMyHours, setSyncToMyHours] = useState(true);
  const [syncToJoosep, setSyncToJoosep] = useState(true);

  const isAccountant = user.userData.userRoles.includes("Accounting");
  const isManager = user.userData.userRoles.includes("Unit Manager");
  const isUnitManager =
    isManager &&
    (user.userData.managedUnits.includes(unit?.unit_id) ||
      project.approvers.find(
        (a) => a.approver_email === user.userData.userName,
      ));
  const isAdmin = user.userData.userRoles.includes("Administrator");

  const canEdit =
    (user.userData.userName === project.project_manager_email &&
      [statuses.DRAFT, statuses.PREPARING_FOR_SIGNOFF].includes(
        status.status_id,
      )) ||
    isAdmin ||
    isAccountant ||
    isUnitManager;

  const canSeeFiles =
    user.userData.userName === project.project_manager_email ||
    isAdmin ||
    isAccountant ||
    isUnitManager;

  const canEditCode =
    (isAccountant || isAdmin) && project.status_id >= statuses.CONFIRMED;

  const canSeeBudget =
    isManager ||
    isAccountant ||
    isAdmin ||
    user.userData.userName === project.project_manager_email;

  const [selectedChange, setSelectedChange] = useState(null);
  /* const [open, setOpen] = useState(false); */
  const theme = useTheme();
  /* const isMdUp = useMediaQuery(theme.breakpoints.up("md")); */

  const handleChange = (e) => {
    const value = e.target.value;
    setSerialNumber(value);
  };

  const handleCodeCancel = () => {
    setCodeFields(project.code, project.unit_id, project.activity_id);
  };

  const handleCodeCancelSubmit = () => {
    if (isCodeValid) return;
    handleCodeGeneration();
  };

  const handleDecisionChange = (value) => {
    setDecision(value);
  };

  const handleCommentsChange = (event) => {
    setComments(event.target.value);
  };

  /* const handleExpand = () => {
    setExpand(!expand);
  }; */

  const refreshDecision = async () => {
    fetchProjectDecisions(uid, setDecisions, setError);
    refreshProject();
  };

  const handleSubmitDecision = async () => {
    submitDecision(
      decision,
      comments,
      setAlert,
      project,
      decisionByRole,
      fixedCode + serialNumber,
      serialNumber,
      syncToMyHours,
      syncToJoosep,
      refreshDecision,
    );
  };

  const handleCodeValidation = async () => {
    setValidating(true);
    if ((fixedCode + serialNumber).length !== 10) {
      setIsCodeValid(false);
      setValidating(false);
      return;
    }
    const valid = await validateCode(
      fixedCode + serialNumber,
      project.project_id,
      setAlert,
    );
    if (valid === true) {
      setIsCodeValid(true);
    } else {
      setIsCodeValid(false);
    }
    await new Promise((resolve) => setTimeout(resolve, 10));
    setValidating(false);
  };

  const handleCodeUpdate = async () => {
    saveCode(
      project,
      fixedCode + serialNumber,
      serialNumber,
      syncToMyHours,
      syncToJoosep,
      setAlert,
      setEditCode,
      refreshProject,
      setCodeFields,
      setError,
    );
  };

  const handleCodeGeneration = async () => {
    const newCode = await generateCode(uid, setAlert, setError);
    setCodeFields(newCode, project.unit_id, project.activity_id);
  };

  const canSeeDecisions =
    user?.userData?.userName === project?.project_manager_email ||
    isUnitManager ||
    isAccountant ||
    isAdmin;

  /* const canSeeChangeLog = isAccountant || isManager || isAdmin; */

  const canShowProgressButton =
    (user?.userData?.userName === project?.project_manager_email ||
      isAdmin ||
      isAccountant ||
      isUnitManager) &&
    [statuses.CONFIRMED, statuses.IN_PROGRESS].includes(project?.status_id);

  /* useEffect(() => {
    setOpen(isMdUp);
  }, [isMdUp]); */

  const changes = useMemo(() => {
    return selectedChange?.changes ?? {};
  }, [selectedChange]);

  const approvals = useMemo(() => {
    const lastNotApprovedDecision = decisions.find(
      (d) => d.decision !== "Approved",
    );
    const approvedCount = decisions.filter((d) =>
      d.decision === "Approved" && lastNotApprovedDecision === undefined
        ? true
        : d.date > lastNotApprovedDecision.date,
    ).length;
    const notApprovedCount = project.approvers.length - approvedCount;
    return { approved: approvedCount, notApproved: notApprovedCount };
  }, [decisions, project.approvers]);

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            mb: 2.5,
            borderRadius: 3,
            border: (t) => `1px solid ${t.palette.divider}`,
            bgcolor: (t) => t.palette.background.paper,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={1.25}
          >
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              flexWrap="wrap"
            >
              {selectedChange ? (
                changes.name ? (
                  <ChangeText
                    newValue={changes.name.new}
                    oldValue={changes.name.old}
                    variant="h5"
                  ></ChangeText>
                ) : (
                  <Typography variant="h5" color="primary" sx={{ mr: 0.5 }}>
                    Untitled Project
                  </Typography>
                )
              ) : (
                <Typography variant="h5" color="primary" sx={{ mr: 0.5 }}>
                  {project.acronym
                    ? `${project.acronym}: ${project.name}`
                    : project.name || "Untitled Project"}
                </Typography>
              )}

              <ProjectStatus approvals={approvals} status={status} />
            </Stack>

            {/* Primary actions (top-right) */}
            <Stack direction="row" spacing={1}>
              {project.requires_progress_reporting &&
                [
                  statuses.CONFIRMED,
                  statuses.IN_PROGRESS,
                  statuses.FINISHED,
                ].includes(project.status_id) && (
                  <SectorCircularProgress
                    values={project.progress.map((r) => ({
                      value: r.progress,
                      total: r.total_progress,
                    }))}
                    label={`${project.total_progress ?? 0}%`}
                  ></SectorCircularProgress>
                )}
              {canShowProgressButton && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() =>
                    updateProjectStatus(project.uid, setAlert, refreshProject)
                  }
                  sx={{ height: "100%" }}
                >
                  {project.status_id === statuses.CONFIRMED
                    ? "Mark as In Progress"
                    : "Mark as Finished"}
                </Button>
              )}

              {canEdit && (
                <Link href={`/project/edit/${project?.uid}`} underline="none">
                  <Button variant="contained" startIcon={<EditRoundedIcon />}>
                    Edit Project
                  </Button>
                </Link>
              )}
            </Stack>
          </Stack>
        </Paper>

        <Stack spacing={2.5}>
          {/* Editable Project Code */}
          {canEditCode && (
            <ProjectCodeBlock
              serialNumber={serialNumber}
              fixedCode={fixedCode}
              handleChange={handleChange}
              editCode={editCode}
              setEditCode={setEditCode}
              handleCodeCancel={handleCodeCancel}
              similarProjects={similarProjects}
              project={project}
              handleCodeUpdate={handleCodeUpdate}
              handleCodeValidation={handleCodeValidation}
              handleCodeGeneration={handleCodeGeneration}
              decisionForm={false}
              validating={validating}
              isCodeValid={isCodeValid}
              syncToMyHours={syncToMyHours}
              setSyncToMyHours={setSyncToMyHours}
              syncToJoosep={syncToJoosep}
              setSyncToJoosep={setSyncToJoosep}
            />
          )}
          {project.requires_progress_reporting &&
            project.status_id >= statuses.CONFIRMED &&
            project.status_id !== statuses.FUNDING_REJECTED &&
            project.status_id !== statuses.IDEA_REJECTED &&
            (user?.userData?.userName === project.project_manager_email ||
              isAccountant ||
              isAdmin ||
              isManager) && (
              <ProjectProgress
                project={project}
                projectProgress={project.progress}
                refreshProject={refreshProject}
                project_id={project.project_id}
                project_start={project.start_date}
                project_end={project.end_date}
                setAlert={setAlert}
                setError={setError}
                isAdmin={isAdmin}
                isAccountant={isAccountant}
                isProjectManager={
                  user?.userData?.userName === project.project_manager_email
                }
              ></ProjectProgress>
            )}

          {/* Decisions list */}
          {canSeeDecisions && (
            <ProjectDecisions
              status={status}
              decisions={decisions}
              showResolved={showResolved}
              setShowResolved={setShowResolved}
              uid={project.uid}
              user={user}
              setDecisions={setDecisions}
              setAlert={setAlert}
              setError={setError}
            />
          )}

          {/* Metadata */}
          <ProjectMetaData
            project={project}
            unit={unit}
            units={units}
            activity={activity}
            activities={activities}
            canEdit={canEdit}
            status={status}
            changes={changes}
            selectedChange={selectedChange}
            ChangeText={ChangeText}
            ChangeTextChip={ChangeTextChip}
            canSeeBudget={canSeeBudget}
          />
          {canSeeFiles && (
            <Attachments
              allowedTypes={allowedTypes}
              allowedExtensions={allowedExtensions}
              attachments={attachments}
              uid={project.uid}
              refreshProject={refreshProject}
            ></Attachments>
          )}
          {/* Decision Form */}
          <ProjectDecisionForm
            isAdmin={isAdmin}
            asAccountant={asAccountant}
            toggleAccountant={toggleAccountant}
            isManager={isUnitManager}
            isAccountant={isAccountant}
            project={project}
            decision={decision}
            handleCommentsChange={handleCommentsChange}
            handleDecisionChange={handleDecisionChange}
            handleSubmitDecision={handleSubmitDecision}
            comments={comments}
            lastDecision={decisions.length === 0 ? null : decisions[0]}
            user={user}
            setDecisionByRole={setDecisionByRole}
            // code edit
            serialNumber={serialNumber}
            fixedCode={fixedCode}
            handleChange={handleChange}
            editCode={editCode}
            setEditCode={setEditCode}
            handleCodeCancel={handleCodeCancelSubmit}
            similarProjects={similarProjects}
            handleCodeValidation={handleCodeValidation}
            handleCodeGeneration={handleCodeGeneration}
            isCodeValid={isCodeValid}
            decisionForm={true}
            validating={validating}
            syncToMyHours={syncToMyHours}
            setSyncToMyHours={setSyncToMyHours}
            syncToJoosep={syncToJoosep}
            setSyncToJoosep={setSyncToJoosep}
          />
        </Stack>

        {/* Spacer at bottom for breathing room */}
        <Box sx={{ height: 16 }} />
      </Container>
      {/* 
      {canSeeChangeLog && !open && (
        <Tooltip title="Open Change Log" arrow>
          <IconButton
            sx={{
              position: "fixed",
              top: `139px`,
              right: 28,
            }}
            size="small"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <MenuRounded /> : <HistoryRounded />}&nbsp;Change log
          </IconButton>
        </Tooltip>
      )}
      {canSeeChangeLog && open && (
        <Paper
          elevation={8}
          sx={(t) => ({
            position: "fixed",
            top: `120px`,
            bottom: 216,
            right: 16,
            width: { xs: "auto", md: 320 },
            overflow: "hidden", // header sticks, list scrolls
            borderRadius: 3,
            zIndex: t.zIndex.appBar - 1,
            display: "flex",
            flexDirection: "column",
          })}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={(t) => ({
              p: 1.5,
              position: "sticky",
              top: 0,
              bgcolor: t.palette.background.paper,
              borderBottom: `1px solid ${t.palette.divider}`,
              zIndex: 1,
            })}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2">Change Log</Typography>
            </Stack>
            <IconButton size="small" onClick={() => setOpen((v) => !v)}>
              {open ? <MenuRounded /> : <MenuRounded />}
            </IconButton>
          </Stack>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              maxHeight: "100%",
              px: 1.5,
              pt: 1.5,
              pb: 1.5,
              // optional scrollbar tidy:
              "&::-webkit-scrollbar": { width: 8 },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: 8,
                background: "rgba(0,0,0,.2)",
              },
              scrollbarWidth: "thin",
            }}
          >
            <Stack spacing={1}>
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="flex-start"
                onClick={() => {
                  setSelectedChange(null);
                }}
                sx={(t) => ({
                  p: 1,
                  borderRadius: 2,
                  cursor: "pointer",
                  position: "relative",
                  bgcolor:
                    selectedChange === null
                      ? alpha(t.palette.primary.main, 0.1)
                      : t.palette.action.hover, // subtle card-like bg
                  "&:hover": {
                    bgcolor:
                      selectedChange === null
                        ? alpha(t.palette.primary.main, 0.2)
                        : alpha(t.palette.action.hover, 0.4),
                  },
                  // left accent bar for selected
                  "&::before":
                    selectedChange === null
                      ? {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 3,
                          borderTopLeftRadius: 8,
                          borderBottomLeftRadius: 8,
                          bgcolor: t.palette.primary.main,
                        }
                      : {},
                })}
              >
                <Box sx={{ mt: "2px" }}>
                  <HistoryRounded fontSize="small" />
                </Box>
                <Stack minWidth={0}>
                  <Typography variant="caption" color="text.secondary">
                    Latest
                  </Typography>
                  <Typography
                    variant="body2"
                    noWrap
                    title={project.updated_by}
                    sx={{ maxWidth: 260 }}
                  >
                    {project.updated_by}
                  </Typography>
                </Stack>
              </Stack>
              {project.logs.map((log, i) => {
                const isSelected =
                  selectedChange?.project_logs_id === log.project_logs_id;

                return (
                  <Stack
                    key={i}
                    direction="row"
                    spacing={1.25}
                    alignItems="flex-start"
                    onClick={() => {
                      if (
                        selectedChange?.project_logs_id === log.project_logs_id
                      ) {
                        setSelectedChange(null);
                      } else {
                        setSelectedChange(log);
                      }
                    }}
                    sx={(t) => ({
                      p: 1,
                      borderRadius: 2,
                      cursor: "pointer",
                      position: "relative",
                      bgcolor: isSelected
                        ? alpha(t.palette.primary.main, 0.1)
                        : t.palette.action.hover, // subtle card-like bg
                      "&:hover": {
                        bgcolor: isSelected
                          ? alpha(t.palette.primary.main, 0.2)
                          : alpha(t.palette.action.hover, 0.4),
                      },
                      // left accent bar for selected
                      "&::before": isSelected
                        ? {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 3,
                            borderTopLeftRadius: 8,
                            borderBottomLeftRadius: 8,
                            bgcolor: t.palette.primary.main,
                          }
                        : {},
                    })}
                  >
                    <Box sx={{ mt: "2px" }}>
                      <HistoryRounded fontSize="small" />
                    </Box>
                    <Stack minWidth={0}>
                      <Typography variant="caption" color="text.secondary">
                        {fmtLocal(log.createdAt)}
                      </Typography>
                      <Typography
                        variant="body2"
                        noWrap
                        title={log.created_by}
                        sx={{ maxWidth: 260 }}
                      >
                        {log.created_by}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        title={`Type: ${log.saved_as}`}
                      >
                        Type: {log.saved_as}
                      </Typography> 
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        </Paper>
      )} */}
    </ThemeProvider>
  );
};

export default DisplayProject;
